import React, { useEffect, useMemo, useState } from "react";
import styles from "./InstantTournaments.module.scss";
import { loader } from "graphql.macro";
import { useQuery } from "../../../hooks/useQuery";
import {
  ScheduledTournamentQuery,
  ScheduledTournamentQueryVariables,
} from "./graphql/CurrentScheduledTournamentQuery.generated";
import { AbsoluteCenter } from "../../AbsoluteCenter";
import { Loader } from "../../Loader";
import { Tab, TabList, TabPanel, TabPanels, Tabs } from "@reach/tabs";
import classNames from "classnames";
import randomPairActive from "./assets/random-pair.png";
import randomPair from "./assets/random-pair-not-active.png";
import { TournamentRoundsSlider } from "./TournamentRoundsSlider";
import roundTournamentImg from "./assets/round-tournament-bg.png";
import { useCurrentRound } from "./hooks/useCurrentRound";
import { useSubscription } from "../../../hooks/useSubscription";
import {
  ScheduledTournamentRoundsSubscription,
  ScheduledTournamentRoundsSubscriptionVariables,
} from "./graphql/ScheduledTournamentRoundsSubscription.generated";
import {
  InstantTournamentRoundMatchUpdate,
  TournamentRoundStage,
  TournamentRoundStatus,
  TournamentStatus,
} from "../../../graphql/generated";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/browser";
import { useHistory, useParams } from "react-router-dom";
import { useUser } from "../../UserProvider";
import infoIcon from "../../../assets/info.svg";
import { ButtonWithSound } from "../../AudioProvider";

const SCHEDULED_TOURNAMENT_QUERY = loader(
  "./graphql/CurrentScheduledTournamentQuery.graphql"
);

const SCHEDULED_TOURNAMENT_SUBSCRIPTION = loader(
  "./graphql/ScheduledTournamentRoundsSubscription.graphql"
);

export const ScheduledTournamentRounds = ({
  onClose,
  roomId,
  tournamentId,
}: {
  onClose: () => void;
  roomId: string;
  tournamentId: string;
}) => {
  const { t } = useTranslation();
  const { push } = useHistory();
  const { user } = useUser();
  const { sectionId } = useParams<{
    sectionId: string;
  }>();
  const [activeTab, setActiveTab] = useState(0);
  const [tournamentData, setTournamentData] = useState<
    ScheduledTournamentQuery
  >();
  const getCurrentRound = useCurrentRound();

  const variables = useMemo(() => ({ id: tournamentId }), [tournamentId]);

  const [{ data, fetching }, reexecuteQuery] = useQuery<
    ScheduledTournamentQuery,
    ScheduledTournamentQueryVariables
  >({
    query: SCHEDULED_TOURNAMENT_QUERY,
    variables,
    pause: true,
  });

  const [{ data: tournamentSubscription, error }] = useSubscription<
    unknown,
    ScheduledTournamentRoundsSubscription,
    ScheduledTournamentRoundsSubscriptionVariables
  >({
    query: SCHEDULED_TOURNAMENT_SUBSCRIPTION,
    variables: { scheduledTournamentId: tournamentId },
  });

  useEffect(() => {
    if (tournamentId && !data?.scheduledTournament?.rounds?.length) {
      reexecuteQuery({ requestPolicy: "network-only" });
    }
  }, [tournamentId, reexecuteQuery]);

  useEffect(() => {
    if (data?.scheduledTournament) {
      setTournamentData(data);
    }
  }, [data, data?.scheduledTournament, data?.scheduledTournament?.status]);

  useEffect(() => {
    if (error) {
      Sentry.captureMessage(
        `Error on scheduled tournament rounds: scheduledTournamentId: ${tournamentId}, errorMessage: ${error.message}`
      );
    }
  }, [error, tournamentId]);

  useEffect(() => {
    const rounds = tournamentData?.scheduledTournament?.rounds;
    const ongoingIndex = rounds?.findIndex(
      (round) => round.status === "ONGOING"
    );

    setActiveTab(
      (ongoingIndex || 0) >= 0 ? ongoingIndex || 0 : (rounds?.length || 1) - 1
    );
  }, [tournamentData]);

  useEffect(() => {
    if (tournamentSubscription) {
      if (
        tournamentSubscription.scheduledTournamentRounds?.__typename ===
        "ScheduledTournamentRoundMatchUpdate"
      ) {
        const newRoundsData = tournamentData?.scheduledTournament?.rounds?.map(
          (round) => {
            if (
              round.stage !==
              tournamentSubscription.scheduledTournamentRounds?.stage
            )
              return round;

            //@ts-ignore
            const instantTournamentRound: InstantTournamentRoundMatchUpdate =
              tournamentSubscription.scheduledTournamentRounds;

            return {
              ...round,
              matches: round.matches.map((match) => {
                return instantTournamentRound.match.ordinalNumber ===
                  match.ordinalNumber
                  ? {
                      ...match,
                      winnerOrdinalNumber:
                        instantTournamentRound.match.winnerOrdinalNumber,
                    }
                  : match;
              }),
            };
          }
        );

        setTournamentData({
          scheduledTournament: {
            ...tournamentData?.scheduledTournament,
            //@ts-ignore
            rounds: newRoundsData,
          },
        });
      }

      if (
        tournamentSubscription.scheduledTournamentRounds?.__typename ===
        "ScheduledTournamentRoundEntityUpdate"
      ) {
        const newTournamentData =
          tournamentSubscription?.scheduledTournamentRounds;

        if (
          newTournamentData?.status === TournamentRoundStatus.Completed &&
          newTournamentData?.stage !== TournamentRoundStage.Final
        ) {
          const ongoingRoundIndex = tournamentData?.scheduledTournament?.rounds?.findIndex(
            (item) => newTournamentData?.stage === item.stage
          );

          const nextStage =
            tournamentData?.scheduledTournament?.rounds?.[
              ongoingRoundIndex! + 1
            ];

          if (
            nextStage?.status! !== TournamentRoundStatus.Ongoing &&
            nextStage?.status! !== TournamentRoundStatus.Completed
          ) {
            reexecuteQuery({ requestPolicy: "network-only" });
          }
        }

        const ongoingRound = tournamentData?.scheduledTournament?.rounds?.findIndex(
          (item) => {
            if (newTournamentData?.status === TournamentRoundStatus.Ongoing) {
              return item.status === TournamentRoundStatus.Ongoing;
            }
          }
        );
        (ongoingRound || 0) > 0 && setActiveTab(ongoingRound || 0);
      }
    }
  }, [
    tournamentSubscription,
    tournamentData?.scheduledTournament,
    tournamentSubscription?.scheduledTournamentRounds,
  ]);

  const handleTabsChange = (index: number) => {
    setActiveTab(index);
  };

  const currentRoundData = useMemo(() => {
    return tournamentData?.scheduledTournament?.rounds[activeTab];
  }, [activeTab, tournamentData]);

  const currentRoundMatchLink = useMemo(() => {
    const match = currentRoundData?.matches?.find((match) =>
      match.teams.some((team) =>
        team.players.some((player) => player?.id === user.id)
      )
    );

    return match && typeof match?.winnerOrdinalNumber !== "number"
      ? `/area/${sectionId}/${match.tableId}`
      : "";
  }, [user, activeTab, currentRoundData]);

  const showStayPopup = useMemo(() => {
    const matchWithPlayer = currentRoundData?.matches?.find((match) =>
      match.teams.some((team) =>
        team.players.some((player) => player?.id === user?.id)
      )
    );

    const nextRound =
      tournamentData?.scheduledTournament?.rounds[activeTab + 1];
    const isNextRoundStarted =
      nextRound?.status === TournamentRoundStatus.Ongoing ||
      nextRound?.status === TournamentRoundStatus.Completed;

    if (isNextRoundStarted) {
      return false;
    } else if (!matchWithPlayer) {
      return false;
    } else {
      const teamWithPlayer = matchWithPlayer.teams?.find((team) =>
        team.players.some((player) => player?.id === user?.id)
      );

      const playerTeamOrdinal = teamWithPlayer?.ordinalNumber;
      const winnerOrdinal = matchWithPlayer.winnerOrdinalNumber;

      return (
        typeof winnerOrdinal === "number" &&
        winnerOrdinal === playerTeamOrdinal &&
        currentRoundData?.stage !== TournamentRoundStage.Final
      );
    }
  }, [user, activeTab, currentRoundData]);

  if (fetching) {
    return (
      <AbsoluteCenter>
        <Loader description="Fetching tournament" />
      </AbsoluteCenter>
    );
  }

  return (
    <div className="h-full flex flex-col gap-5">
      {showStayPopup && (
        <div className={styles.popup}>
          <img src={infoIcon} width={44} height={44} alt="" />
          <div className={styles.popup_info}>
            <p>{t("instant_tournament.match_finished")}</p>
            <p>{t("instant_tournament.stay_in_room")}</p>
          </div>
        </div>
      )}
      <div>
        <button
          className="flex items-center text-gray-500 gap-2"
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
          <span>{t("instant_tournament.back_to_my_tournaments")}</span>
        </button>
      </div>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <div
            className={styles.roundImage}
            style={{
              backgroundImage: `url(${roundTournamentImg})`,
              backgroundSize: "contain",
            }}
          >
            <img
              src={`${process.env.REACT_APP_API_HOST}/scheduled-tournaments/${tournamentData?.scheduledTournament?.image}`}
              alt="img"
            />
          </div>
          <span className="text-white text-2xl font-semibold">
            {tournamentData?.scheduledTournament?.name}
          </span>
        </div>
      </div>
      <div className="h-full flex">
        <div className="h-full w-full">
          <Tabs
            className="flex flex-col gap-4 h-full"
            index={activeTab}
            onChange={handleTabsChange}
          >
            <div className="flex items-center justify-between">
              <TabList
                className={classNames("flex", "gap-3", "p-1", "rounded-md")}
              >
                {tournamentData?.scheduledTournament?.rounds?.map(
                  (round, index) => {
                    return (
                      <Tab
                        key={`${round?.stage}_tab`}
                        className={classNames(
                          "flex",
                          "items-center",
                          "py-1",
                          "px-2",
                          "rounded-md",
                          styles.roundTab,
                          {
                            [styles.active]: activeTab === index,
                          }
                        )}
                        style={{
                          gap: "0.375rem",
                        }}
                      >
                        <div>
                          <img
                            style={{ width: "1.5rem", height: "1.5rem" }}
                            src={
                              activeTab === index
                                ? randomPairActive
                                : randomPair
                            }
                            alt="icon"
                          />
                        </div>
                        {getCurrentRound(round.stage)}
                      </Tab>
                    );
                  }
                )}
              </TabList>
              {currentRoundData?.status === TournamentRoundStatus.Ongoing &&
                currentRoundMatchLink && (
                  <ButtonWithSound
                    onClick={() => {
                      push(currentRoundMatchLink);
                    }}
                    className={classNames(
                      "px-4",
                      "text-white",
                      "rounded-md",
                      "font-semibold",
                      "btn",
                      "btn-green-600",
                      "w-fit",
                      "text-base"
                    )}
                  >
                    {t("instant_tournament.play_round")}
                  </ButtonWithSound>
                )}
            </div>

            <TabPanels className={classNames(styles.tournamentsWrap)}>
              {tournamentData?.scheduledTournament?.rounds?.map((round) => {
                return (
                  <TabPanel className="h-full" key={`${round?.stage}_tabpanel`}>
                    <TournamentRoundsSlider
                      hideNextArrow={false}
                      hidePrevArrow={false}
                      //@ts-ignore
                      round={round}
                      isOneVSOne={
                        tournamentData?.scheduledTournament?.playerPerTeam!
                      }
                    />
                  </TabPanel>
                );
              })}
            </TabPanels>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
